/**
 * Secret fields — admin-defined catalogue of (key, label, type) entries.
 *
 * Each org fills in its own value for each defined field via the per-org
 * Secrets section. Capability configs reference these keys via
 * `{"$secret": "<key>"}` so values get injected at task execution time.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import { listGlobalSecrets } from '@optum/api-client/endpoints/global-secrets';
import {
  deleteSecretField,
  listSecretFields,
} from '@optum/api-client/endpoints/secret-fields';
import type { SecretField } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { Button } from '../../ui/Button.js';
import { CredentialTable, type CredentialRow } from '../../ui/CredentialTable.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useConfirm } from '../../ui/Confirm.js';
import { SectionHeader } from '../../ui/SectionHeader.js';

export function SecretFieldsPage() {
  const panel = useRecordPanel();
  const { isPlatformAdmin } = useAuth();
  const query = useQuery({ queryKey: qk.secretFields(), queryFn: listSecretFields });
  // Shares the Platform values tab's cache: the two tabs are the definition and
  // the value behind it, and a definition with no value is the one thing this
  // list can warn about before a task fails on it.
  const values = useQuery({ queryKey: qk.globalSecrets(), queryFn: listGlobalSecrets });
  const valueSet = new Set((values.data ?? []).filter((v) => v.is_set).map((v) => v.field_key));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Secret fields"
        subtitle="Catalogue of named values each organization can fill in. Referenced from capability configs as $secret and resolved at task time."
        actions={
          isPlatformAdmin && (
            <Button variant="primary" icon="add" onClick={() => panel.openForm({ name: 'new-secret-field' })}>
              Define field
            </Button>
          )
        }
      />
      <>
        {query.isPending && <LoadingBlock />}
        {query.isError && (
          <ErrorBlock
            message={query.error instanceof Error ? query.error.message : 'Failed to load'}
          />
        )}
        {query.data?.length === 0 && <EmptyBlock>No fields defined yet.</EmptyBlock>}
        {query.data && query.data.length > 0 && (
          <CredentialTable
            rows={query.data.map<CredentialRow>((f) => ({
              id: f.id,
              name: f.key,
              sub: f.label,
              scope: f.is_secret ? `${f.type} · secret` : f.type,
              // A definition with no platform value behind it is the failure
              // this page can see coming: it resolves at task time, not here.
              status: valueSet.has(f.key)
                ? { label: 'value set', tone: 'ok' }
                : { label: 'no value', tone: 'neutral' },
              ...(isPlatformAdmin ? { actions: <DeleteFieldButton field={f} /> } : {}),
            }))}
            count={`${query.data.length} field${query.data.length === 1 ? '' : 's'}`}
            hint="an organization's own value wins over the platform fallback"
          />
        )}
      </>
    </div>
  );
}

function DeleteFieldButton({ field }: { field: SecretField }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const remove = useMutation({
    mutationFn: () => deleteSecretField(field.id),
    onSuccess: () => {
      toast.success(`Deleted ${field.key}`);
      qc.invalidateQueries({ queryKey: qk.secretFields() });
    },
    onError: (e) => toast.error('Delete failed', e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <Button
      size="sm"
      variant="danger"
      onClick={() => {
        void confirm({
          title: `Delete field ${field.key}?`,
          description:
            'Per-org values for this field stay in the DB but become orphaned. Capability configs that reference this key will fail at task time with "secret not set".',
          confirmLabel: 'Delete',
          tone: 'danger',
        }).then((ok) => {
          if (ok) remove.mutate();
        });
      }}
    >
      Delete
    </Button>
  );
}

